
import { Link } from "react-router-dom"
import {
  Building2,
  HeartPulse,
  Microscope,
  Stethoscope,
  Activity,
  CheckCircle,
  Bed,
  Pill,
  TestTube2,
  Calendar,
  ArrowRight,
  Zap,
  Shield,
  Award,
  Users,
} from "lucide-react"

import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import SEO from "../SEO"

export default function InfrastructurePage() {
  const infrastructureSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad offering advanced hospital infrastructure and patient-focused medical facilities.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "telephone": "+91-99660-60000",
      "availableLanguage": ["English", "Telugu", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaamhospitals.com/about/infrastructure"
    },
    "headline": "World-Class Hospital Infrastructure at Pranaam Hospitals",
    "description": "Discover the advanced medical infrastructure at Pranaam Hospitals including modern operation theatres, ICUs, diagnostics, emergency services, and patient-friendly hospital design.",
    "image": "https://pranaamhospitals.com/wp-content/uploads/2025/hospital-infrastructure.jpg",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals infrastructure",
      "hospital infrastructure Hyderabad",
      "advanced medical infrastructure Hyderabad",
      "NABH accredited hospital infrastructure"
    ]
  }
];



  const highlights = [
    { label: "ICU Beds", value: "36", icon: Bed },
    { label: "Emergency Beds", value: "5", icon: Activity },
    { label: "Operating Theaters", value: "4", icon: HeartPulse },
    { label: "24/7 Services", value: "Lab & Pharmacy", icon: Pill },
  ]

  const icuUnits = [
    {
      name: "Critical Intensive Care Unit (CICU)",
      description: "Advanced cardiac critical care with continuous monitoring",
      icon: HeartPulse,
    },
    {
      name: "Surgical Intensive Care Unit (SICU)",
      description: "Post-operative critical care for surgical patients",
      icon: Activity,
    },
    {
      name: "Medical Intensive Care Unit (MICU)",
      description: "Comprehensive medical critical care services",
      icon: Stethoscope,
    },
    {
      name: "Maternity Intensive Care Unit",
      description: "Specialized critical care for high-risk maternity cases",
      icon: HeartPulse,
    },
    {
      name: "High Dependency Unit (HDU)",
      description: "Intermediate care between ICU and general wards",
      icon: Activity,
    },
    {
      name: "Neonatal Intensive Care Unit (NICU)",
      description: "Advanced care for critically ill newborns",
      icon: HeartPulse,
    },
  ]

  const imagingServices = [
    { name: "CT Scan", description: "Advanced computed tomography imaging" },
    { name: "Echo", description: "Echocardiography for cardiac assessment" },
    { name: "Color Doppler", description: "Blood flow visualization studies" },
    {
      name: "Performance Echocardiography System",
      description: "With adult and pediatric probes",
    },
    { name: "Tread Mill Test", description: "Cardiac stress testing" },
    { name: "Electrocardiography", description: "ECG for heart rhythm analysis" },
    { name: "Bone Densitometry", description: "Bone density assessment" },
    { name: "X-Ray", description: "Digital radiography services" },
    { name: "Ultrasonography 4D", description: "Advanced 4D ultrasound imaging" },
  ]

  const labServices = [
    { name: "Cytology", icon: TestTube2 },
    { name: "Histopathology", icon: Microscope },
    { name: "Immunohistochemistry", icon: TestTube2 },
    { name: "Cytochemistry", icon: Microscope },
    { name: "Tumour Markers", icon: TestTube2 },
    { name: "Haematology", icon: Microscope },
    { name: "Biochemistry", icon: TestTube2 },
    { name: "Clinical Pathology", icon: Microscope },
    { name: "Microbiology & Serology", icon: TestTube2 },
  ]

  return (
    <>
    <SEO
  title="Hospital Infrastructure | World-Class Medical Setup at Pranaam Hospitals"
  description="Explore the world-class hospital infrastructure at Pranaam Hospitals, Hyderabad, featuring advanced operation theatres, ICUs, diagnostics, emergency care, and patient-centric facilities."
  canonical="https://pranaamhospitals.com/about/infrastructure"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals infrastructure, hospital infrastructure Hyderabad,
            advanced hospital setup Hyderabad, ICU infrastructure Hyderabad,
            NABH hospital infrastructure, medical infrastructure Hyderabad"
  schema={infrastructureSchema}
/>

    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/10 to-primary/5">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">State-of-the-Art Infrastructure</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                World-Class <span className="text-primary">Medical Infrastructure</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                While Pranaam credits its ongoing evolution and success to the exemplary skills and expertise of its
                team, they wouldn't be able to provide top-quality treatments, care and services without our
                state-of-the-art infrastructure that boasts of features meeting national and international quality
                standards. Catering to all kinds of illnesses, surgeries and emergencies, we have ample resources
                ensuring that you have the finest quality care and treatment from the best hands in medicine in
                Hyderabad.
              </p>
            </div>
          </div>
        </section>

        {/* Highlights Section */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <Badge className="mb-2">Key Highlights</Badge>
                <h2 className="text-3xl font-bold mb-4">Infrastructure at a Glance</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {highlights.map((highlight, index) => {
                  const Icon = highlight.icon
                  return (
                    <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-3xl font-bold text-primary mb-2">{highlight.value}</h3>
                        <p className="text-gray-600">{highlight.label}</p>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Additional Highlights */}
        <section className="py-12 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">IP Storage Room & Archival Facility</h3>
                      <p className="text-gray-600 text-sm">
                        Secure digital storage and archival system for patient records and medical imaging
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Research & Academic Excellence</h3>
                      <p className="text-gray-600 text-sm">
                        Academic, national and international research studies by experienced doctors
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Specialist ICU Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4">Critical Care Excellence</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Specialist <span className="text-primary">Intensive Care Units</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Our comprehensive range of specialized ICUs equipped with advanced monitoring systems and life support
                  technologies, staffed by experienced critical care specialists available 24/7.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {icuUnits.map((unit, index) => {
                  const Icon = unit.icon
                  return (
                    <Card key={index} className="group hover:shadow-lg transition-all hover:translate-y-[-4px]">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary transition-colors">
                            <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold mb-2 group-hover:text-primary transition-colors">{unit.name}</h3>
                            <p className="text-sm text-gray-600">{unit.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Diagnostic Facilities */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4">Advanced Diagnostics</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  <span className="text-primary">Diagnostic</span> Facilities
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Comprehensive imaging and diagnostic services with state-of-the-art equipment for accurate and timely
                  diagnosis.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6 text-center">Imaging Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {imagingServices.map((service, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Microscope className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm mb-1">{service.name}</h4>
                            <p className="text-xs text-gray-600">{service.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Card className="bg-white border-primary/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <TestTube2 className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Clinical Laboratory</h3>
                      <p className="text-gray-700">
                        X-Ray and comprehensive clinical laboratory services for accurate diagnostic testing
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Lab Services Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4">Laboratory Medicine</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Comprehensive <span className="text-primary">Lab Services</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Advanced laboratory services with cutting-edge technology and expert pathologists for accurate
                  diagnostic results.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {labServices.map((service, index) => {
                  const Icon = service.icon
                  return (
                    <Card key={index} className="text-center hover:shadow-md transition-shadow group">
                      <CardContent className="p-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary mb-3 transition-colors">
                          <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
                        </div>
                        <p className="font-bold text-sm group-hover:text-primary transition-colors">{service.name}</p>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Quality Standards */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="relative h-120 rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/wp-content/uploads/2026/05/MedicalEquipment.webp"
                    alt="Medical Equipment"
                    fill
                    className="object-cover"
                  />
                 
                </div>
                <div>
                  <Badge className="mb-4">Excellence in Healthcare</Badge>
                  <h2 className="text-3xl font-bold mb-6">
                    Meeting <span className="text-primary">National & International Standards</span>
                  </h2>
                  <p className="text-gray-700 mb-6">
                    Our infrastructure is designed and maintained to meet the highest national and international quality
                    standards, ensuring world-class healthcare delivery.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold mb-1">NABH Accredited</h3>
                        <p className="text-gray-600 text-sm">
                          National accreditation ensuring the highest standards of patient care
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Award className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold mb-1">NABL Certified Labs</h3>
                        <p className="text-gray-600 text-sm">Laboratory services meeting international standards</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Zap className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold mb-1">24/7 Operations</h3>
                        <p className="text-gray-600 text-sm">
                          Round-the-clock availability of critical services and support
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Operating Theaters Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <Card className="bg-gradient-to-br from-gray-50 to-white border-2 border-primary/10">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                      <Badge className="mb-4">Surgical Excellence</Badge>
                      <h2 className="text-2xl font-bold mb-4">
                        4 Well-Equipped <span className="text-primary">Operating Theaters</span>
                      </h2>
                      <p className="text-gray-700 mb-6">
                        Modern operating theaters equipped with laminar airflow systems ensuring the highest level of
                        sterility and patient safety during surgical procedures.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm text-gray-700">Laminar airflow technology</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm text-gray-700">Advanced surgical equipment</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm text-gray-700">HEPA filtration systems</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm text-gray-700">State-of-the-art monitoring systems</span>
                        </div>
                      </div>
                    </div>
                    <div className="relative h-64 md:h-full rounded-lg overflow-hidden shadow-lg">
                      <img
                        src="/wp-content/uploads/2026/05/OperatingTheater.webp"
                        alt="Operating Theater"
                        fill
                        className="object-cover"
                      />
                     
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <Card class="max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary/90 rounded-lg shadow-xl md:p-12 text-white text-center">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Experience World-Class Healthcare Infrastructure</h2>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                  Schedule a visit to our hospital and witness our state-of-the-art infrastructure, advanced medical
                  technology, and commitment to excellence in healthcare delivery.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button size="lg" variant="secondary" className="shadow-lg" asChild>
                      <a
                        href="/book-now"
                        rel="noopener noreferrer"
                      >
                      <Calendar className="w-5 h-5 mr-2" />
                      Book an Appointment
                    </a>
                  </Button>
                 
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}
